import React, { useState, useCallback } from "react";
import { Search, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "./dialog";
import { Input } from "./input";
import { Typography } from "./typography";
import { Asset } from "../../types/asset";
import { useAssets } from "../../hooks/useAssets";
import { VALIDATION_MESSAGES, UI_CONFIG } from "../../constants/autostack";

interface AssetSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  assets: Asset[];
  onAssetSelect: (asset: Asset) => void;
  selectedAsset?: Asset;
  title?: string;
}

const AssetListItem: React.FC<{
  asset: Asset;
  isSelected: boolean;
  onSelect: (asset: Asset) => void;
}> = React.memo(({ asset, isSelected, onSelect }) => {
  const handleClick = useCallback(() => {
    onSelect(asset);
  }, [asset, onSelect]);

  return (
    <div
      onClick={handleClick}
      className={`
        flex items-center justify-between p-3 rounded-lg border cursor-pointer 
        transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500
        ${isSelected ? "border-purple-200 bg-purple-50" : "border-gray-200"}
      `}
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      aria-label={`Select ${asset.assetName}`}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div className="flex items-center space-x-3">
        <img
          src={asset.image}
          alt={asset.assetName}
          className="w-6 h-6 rounded-full"
        />
        <div>
          <Typography variant="p" className="font-medium">
            {asset.asset.toUpperCase()}
          </Typography>
          <Typography variant="p" className="text-sm text-muted-foreground">
            {asset.assetName}
          </Typography>
        </div>
      </div>
      {/* <Typography variant="p" className="text-sm text-muted-foreground">
        R5,000 = {asset.asset} (rate from API)*
      </Typography> */}
    </div>
  );
});

AssetListItem.displayName = "AssetListItem";

export const AssetSelectionModal: React.FC<AssetSelectionModalProps> =
  React.memo(
    ({
      isOpen,
      onClose,
      assets,
      onAssetSelect,
      selectedAsset,
      title = "Select an asset to send",
    }) => {
      const [searchTerm, setSearchTerm] = useState("");
      const { filteredAssets, hasResults } = useAssets({
        searchTerm,
        selectedAssetValue: selectedAsset?.asset,
      });

      const handleAssetSelect = useCallback(
        (asset: Asset) => {
          onAssetSelect(asset);
          onClose();
        },
        [onAssetSelect, onClose]
      );

      const handleSearchChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
          setSearchTerm(e.target.value);
        },
        []
      );

      return (
        <Dialog open={isOpen} onOpenChange={onClose} modal={false}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <DialogTitle className="text-lg font-semibold">
                {title}
              </DialogTitle>
              <DialogClose
                className="rounded-full border border-gray-200 p-2 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                aria-label="Close modal"
              >
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </DialogClose>
            </DialogHeader>

            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search assets..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="pl-10 h-12 rounded-full"
                  aria-label="Search for assets"
                />
              </div>

              <Typography
                variant="p"
                className="text-xs text-center text-muted-foreground"
              >
                *Conversions are based on current market rates and may vary.
              </Typography>

              <div
                className={`space-y-2 ${UI_CONFIG.MODAL_MAX_HEIGHT} overflow-y-auto`}
              >
                {filteredAssets.map((asset) => (
                  <AssetListItem
                    key={asset._id}
                    asset={asset}
                    isSelected={
                      selectedAsset?.asset.toLowerCase() ===
                      asset.asset.toLowerCase()
                    }
                    onSelect={handleAssetSelect}
                  />
                ))}
              </div>

              {!hasResults && searchTerm && (
                <div className="text-center py-6">
                  <Typography variant="p" className="text-muted-foreground">
                    {VALIDATION_MESSAGES.NO_ASSETS_FOUND} "{searchTerm}"
                  </Typography>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      );
    }
  );

AssetSelectionModal.displayName = "AssetSelectionModal";
